import {
  MANAGED_PROPERTY_FIELDS,
  externalSourceLabels,
  formatDate,
  formatTime,
  unitAvailabilityLabels,
  type ExternalSourceInfo,
  type UnitAvailability,
} from '@brimo/shared';
import type { Tone } from './types';

/**
 * Availability of a unit (status × sales release × external block, `unitAvailability` in @brimo/shared) as
 * a status pill, and the lock of the fields an external system owns while its connection is `import_only`.
 */

export const availabilityTones: Record<UnitAvailability, Tone> = {
  available: 'neutral',
  reserved: 'accent',
  sold: 'success',
  unavailable: 'warning',
  not_released: 'warning',
  external_reserved: 'info',
  external_exclusive: 'info',
  external_sold: 'danger',
};

export function availabilityPill(availability: UnitAvailability): { label: string; tone: Tone } {
  return { label: unitAvailabilityLabels[availability], tone: availabilityTones[availability] };
}

/** Selectable for a reservation or a deal only when the availability is `available`. */
export function isSelectable(availability: UnitAvailability): boolean {
  return availability === 'available';
}

/** True when `field` is owned by the external system and the binding is still one-way (`import_only`). */
export function isManagedField(external: ExternalSourceInfo, field: string): boolean {
  return external !== null && external.mode === 'import_only' && external.managedFields.includes(field);
}

/** True when any field is locked (the record shows the lock hint and the source badge tooltip). */
export function hasManagedFields(external: ExternalSourceInfo): boolean {
  return external !== null && external.mode === 'import_only' && external.managedFields.length > 0;
}

/**
 * The PATCH body without the locked fields: a dialog always sends its whole draft, the server rejects any
 * managed field with 409 `EXTERNAL_MANAGED_FIELD` — the locked fields are shown read-only and never sent.
 */
export function withoutManagedFields<T extends object>(body: T, external: ExternalSourceInfo): Partial<T> {
  if (!hasManagedFields(external)) return body;
  const kept = Object.entries(body).filter(([key]) => !isManagedField(external, key));
  return Object.fromEntries(kept) as Partial<T>;
}

/** „Aus TeamProQ" */
export function sourceBadgeLabel(external: NonNullable<ExternalSourceInfo>): string {
  return `Aus ${externalSourceLabels[external.source]}`;
}

/** „Zuletzt abgeglichen: 12.10.2026, 14:05" / „Noch nicht abgeglichen" (+ the gone note). */
export function sourceBadgeTooltip(external: NonNullable<ExternalSourceInfo>): string {
  const synced = external.lastSyncedAt
    ? `Zuletzt abgeglichen: ${formatDate(external.lastSyncedAt)}, ${formatTime(external.lastSyncedAt)}`
    : 'Noch nicht abgeglichen';
  const gone = external.goneAt
    ? ` · Seit ${formatDate(external.goneAt)} nicht mehr in ${externalSourceLabels[external.source]} vorhanden`
    : '';
  return `${synced}${gone}`;
}

/** „Wird aus TeamProQ übernommen" — the hint under a locked field. */
export function managedHint(external: NonNullable<ExternalSourceInfo>): string {
  return `Wird aus ${externalSourceLabels[external.source]} übernommen`;
}

/** „Aus TeamProQ übernommen – wird bei jedem Abgleich aktualisiert." — a locked field of the base-data sheet. */
export function syncedHint(external: NonNullable<ExternalSourceInfo>): string {
  return `Aus ${externalSourceLabels[external.source]} übernommen – wird bei jedem Abgleich aktualisiert.`;
}

/** „Von TeamProQ nicht geliefert – hier pflegen." — a managed property field the import left empty (D-318). */
export function undeliveredHint(external: NonNullable<ExternalSourceInfo>): string {
  return `Von ${externalSourceLabels[external.source]} nicht geliefert – hier pflegen.`;
}

/**
 * True for a property field the external system owns in principle (`MANAGED_PROPERTY_FIELDS`) but did not deliver,
 * so it is maintained in DAMOKLES until an import delivers it (D-318).
 */
export function isUndeliveredManagedField(external: ExternalSourceInfo, field: string): boolean {
  return (
    external !== null &&
    external.mode === 'import_only' &&
    external.goneAt === null &&
    MANAGED_PROPERTY_FIELDS[external.source].includes(field) &&
    !external.managedFields.includes(field)
  );
}
